import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { useToast } from '@/hooks/use-toast';
import { Mic, Heart, Briefcase, Smile } from 'lucide-react';

export const VoiceTheraPreferences = () => {
  const { preferences, updatePreference, updatingPreference } = useOptimizedUserPreferences();
  const { toast } = useToast();
  
  // Get current preferences
  const currentIntroductionPref = preferences.find(p => p.preference_type === 'voicethera_introduction');
  const currentStylePref = preferences.find(p => p.preference_type === 'voicethera_introduction_style');
  
  const [includeIntroduction, setIncludeIntroduction] = useState(
    currentIntroductionPref?.preference_value !== 'false'
  );
  const [introductionStyle, setIntroductionStyle] = useState<'warm' | 'professional' | 'friendly'>(
    (currentStylePref?.preference_value as 'warm' | 'professional' | 'friendly') || 'warm'
  );
  
  useEffect(() => {
    setIncludeIntroduction(currentIntroductionPref?.preference_value !== 'false');
    setIntroductionStyle((currentStylePref?.preference_value as 'warm' | 'professional' | 'friendly') || 'warm');
  }, [currentIntroductionPref, currentStylePref]);

  const handleIntroductionToggle = async (enabled: boolean) => {
    setIncludeIntroduction(enabled);
    
    try {
      await updatePreference({
        category: 'general',
        preferenceType: 'voicethera_introduction',
        value: enabled.toString(),
        confidenceBoost: 1.0
      });
      
      toast({
        title: "Preference updated",
        description: enabled 
          ? "VoiceThera will introduce herself in your sessions"
          : "VoiceThera will start sessions without introduction",
      });
    } catch (error) {
      setIncludeIntroduction(!enabled); // Revert on error
      toast({
        title: "Error",
        description: "Failed to update preference. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStyleChange = async (style: 'warm' | 'professional' | 'friendly') => {
    setIntroductionStyle(style);
    
    try {
      await updatePreference({
        category: 'general',
        preferenceType: 'voicethera_introduction_style',
        value: style,
        confidenceBoost: 1.0
      });
      
      toast({
        title: "Introduction style updated",
        description: `VoiceThera will use a ${style} introduction style`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update introduction style. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStyleIcon = (style: string) => {
    switch (style) {
      case 'warm': return <Heart className="h-4 w-4" />;
      case 'professional': return <Briefcase className="h-4 w-4" />;
      case 'friendly': return <Smile className="h-4 w-4" />;
      default: return <Mic className="h-4 w-4" />;
    }
  };

  const getStyleDescription = (style: string) => {
    switch (style) {
      case 'warm': return 'Caring and nurturing tone';
      case 'professional': return 'Confident and expert guidance';
      case 'friendly': return 'Casual and approachable manner';
      default: return '';
    }
  };

  const getExampleIntroduction = (style: string) => {
    switch (style) {
      case 'warm': return '"Hi there, I\'m VoiceThera, and I\'m here to guide you through this meditation session..."';
      case 'professional': return '"Welcome, I\'m VoiceThera, your personal meditation guide for this session..."';
      case 'friendly': return '"Hello! I\'m VoiceThera, and I\'ll be with you for this meditation session..."';
      default: return '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="h-5 w-5" />
          VoiceThera Introduction
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="include-introduction" className="text-base font-medium">
              Include VoiceThera Introduction
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow VoiceThera to introduce herself at the beginning of sessions
            </p>
          </div>
          <Switch
            id="include-introduction"
            checked={includeIntroduction}
            onCheckedChange={handleIntroductionToggle}
            disabled={updatingPreference}
          />
        </div>

        {includeIntroduction && (
          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label className="text-base font-medium">Introduction Style</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Choose how VoiceThera introduces herself in your sessions
              </p>
              
              <Select value={introductionStyle} onValueChange={handleStyleChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select introduction style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Warm & Caring
                    </div>
                  </SelectItem>
                  <SelectItem value="professional">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Professional & Expert
                    </div>
                  </SelectItem>
                  <SelectItem value="friendly">
                    <div className="flex items-center gap-2">
                      <Smile className="h-4 w-4" />
                      Friendly & Casual
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                {getStyleIcon(introductionStyle)}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{introductionStyle}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {getStyleDescription(introductionStyle)}
                    </span>
                  </div>
                  <p className="text-sm">
                    <strong>Example:</strong> {getExampleIntroduction(introductionStyle)}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              <p><strong>Note:</strong> Some categories like "ZEN AF" and "Breathing Guidance" may skip introductions to maintain their specific tone and flow.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};